/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

// `init.entry` reaches this module synchronously to decide which bundle to boot,
// so everything here must stay free of built-ins that `@babel/preset-env` will
// polyfill (`Array#includes`, `Array#map`, `Array#find`, ...). A single core-js
// import here lands in the entry's chunk graph and drags the shared vendor chunk
// onto the critical path of every page. Locale helpers that need those built-ins
// belong in `./locale` instead.

export const SUPPORTED_LOCALES = ['de', 'en', 'es', 'fr', 'ja', 'ko', 'zh', 'pt'] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: SupportedLocale = 'en'

// URL prefixes mirror the corporate site for Chinese and Japanese. Intl locales
// stay `zh` and `ja`; `cn` and `jp` are path segments only.
const LOCALE_PATH_CODES = {
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  ja: 'jp',
  ko: 'ko',
  pt: 'pt',
  zh: 'cn',
} as const satisfies Record<SupportedLocale, string>

const LOCALE_FROM_PATH_CODE: Record<string, SupportedLocale> = {
  cn: 'zh',
  de: 'de',
  en: 'en',
  es: 'es',
  fr: 'fr',
  ja: 'ja',
  jp: 'ja',
  ko: 'ko',
  pt: 'pt',
  zh: 'zh',
}

export function getLocalePathCode(locale: SupportedLocale): string {
  return LOCALE_PATH_CODES[locale]
}

export function getLocaleFromPathCode(pathCode: string): SupportedLocale | undefined {
  return LOCALE_FROM_PATH_CODE[pathCode]
}

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/'
  }

  const withoutTrailingSlash = pathname.replace(/\/+$/, '')

  return withoutTrailingSlash || '/'
}

export function getLocaleFromPathname(pathname: string): {
  locale: SupportedLocale
  pathname: string
} {
  const normalizedPathname = normalizePathname(pathname)

  if (normalizedPathname === '/') {
    return { locale: DEFAULT_LOCALE, pathname: '/' }
  }

  const [, firstSegment, ...rest] = normalizedPathname.split('/')

  const locale = firstSegment ? getLocaleFromPathCode(firstSegment) : undefined

  if (!firstSegment || !locale) {
    return { locale: DEFAULT_LOCALE, pathname: normalizedPathname }
  }

  const strippedPathname = rest.length > 0 ? `/${rest.join('/')}` : '/'

  return {
    locale,
    pathname: strippedPathname,
  }
}

export function stripLocaleFromPath(pathname: string): string {
  return getLocaleFromPathname(pathname).pathname
}

export function addLocaleToPath(pathname: string, locale: SupportedLocale): string {
  const normalizedPathname = normalizePathname(pathname)
  const strippedPathname = stripLocaleFromPath(normalizedPathname)

  if (locale === DEFAULT_LOCALE) {
    return strippedPathname
  }

  const pathCode = getLocalePathCode(locale)

  if (strippedPathname === '/') {
    return `/${pathCode}`
  }

  return `/${pathCode}${strippedPathname}`
}

export function getLocaleDisplayCode(locale: SupportedLocale): string {
  return getLocalePathCode(locale).toUpperCase()
}
